import { CommonErrorMsg } from 'aa_common/front-end/antd'
import { toInt } from 'aa_common/front-end/helpers'
import classNames from 'classnames'
import { getIn, useFormikContext } from 'formik'
import { get } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import NumberFormat, { NumberFormatValues } from 'react-number-format'

import { Wrapper } from './styles'

type Props = {
  isShowError?: boolean
  name: string
  placeholder?: string
  style?: React.CSSProperties
  disabled?: boolean
  minValue?: number
  maxValue?: number
  currencyPrefix?: string
  className?: string
  defaultValue?: number
  onChange?: (value?: number) => void
}

function getNumberInRange(value: number, minValue?: number, maxValue?: number): number {
  let newValue = value
  if (minValue !== undefined && newValue < minValue) {
    newValue = minValue
  }
  if (maxValue !== undefined && newValue > maxValue) {
    newValue = maxValue
  }
  return newValue
}

const MoneyInput: React.FC<Props> = ({
  name = '',
  isShowError = true,
  currencyPrefix = '¥',
  disabled = false,
  placeholder = '¥0',
  style,
  minValue,
  maxValue,
  className,
  defaultValue = 0,
  onChange,
}) => {
  const { values, errors, setFieldValue } = useFormikContext()
  const [value, setValue] = useState(getIn(values, name))
  const [previousValue, setPreviousValue] = useState(getIn(values, name))
  const fieldValue = getIn(values, name)
  const error = get(errors, name)

  useEffect(() => {
    if (!Number.isNaN(toInt(String(fieldValue)))) {
      let newValue = toInt(String(fieldValue))
      newValue = getNumberInRange(newValue, minValue, maxValue)

      if (previousValue !== newValue) {
        setPreviousValue(newValue)
        setFieldValue(name, newValue)
        onChange?.(newValue)
      }
    }
  }, [minValue, maxValue])

  const handleChange = useCallback(
    (event: NumberFormatValues) => {
      const newValue = event.floatValue ?? defaultValue
      const newValueFormat = getNumberInRange(newValue, minValue, maxValue)
      setValue(newValueFormat)
    },
    [minValue, maxValue, defaultValue]
  )

  const handleBlur = useCallback(() => {
    if (previousValue !== value) {
      setPreviousValue(value)
      setFieldValue(name, value)
      onChange?.(value)
    }
  }, [previousValue, value, onChange])

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        setPreviousValue(value)
        setFieldValue(name, value)
        onChange?.(value)
      }
      if (event.key === '.') {
        event.preventDefault()
      }
    },
    [name, value]
  )

  const handleAfterFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => event.target.select())
  }

  useEffect(() => {
    setPreviousValue(fieldValue)
    setValue(fieldValue)
  }, [fieldValue])

  return (
    <Wrapper>
      <NumberFormat
        name={name}
        disabled={disabled}
        placeholder={placeholder}
        value={value ?? ''}
        onValueChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleAfterFocus}
        onKeyPress={handleKeyPress}
        allowNegative={false}
        allowLeadingZeros={false}
        allowEmptyFormatting={false}
        thousandSeparator
        prefix={currencyPrefix}
        defaultValue={defaultValue}
        style={style}
        className={classNames(className, { error, disabled })}
      />
      {error && isShowError && <CommonErrorMsg>{error}</CommonErrorMsg>}
    </Wrapper>
  )
}

export default MoneyInput
